<template>
  <div class="filters">
    <v-card class="pa-5">
      <v-text-field
        v-model="$parent.$parent.$parent.$parent.search"
        prepend-inner-icon="mdi-magnify"
        class="mb-5"
        outlined
        hide-details
        :label="$t('dashboard.tabFilter.label.search')"
      />

      <v-autocomplete
        v-model="$parent.$parent.$parent.$parent.selectedTypes"
        class="mb-5"
        clear-icon="mdi-close-circle"
        item-value="value"
        item-text="name"
        chips
        filled
        outlined
        multiple
        clearable
        hide-details
        return-object
        deletable-chips
        small-chips
        :items="serviceTypes"
        :disabled="isLoading"
        :item-text="(item) => $t(`type.${item.value}`)"
        :label="$t('dashboard.tabFilter.label.serviceType')"
      />

      <v-autocomplete
        v-model="$parent.$parent.$parent.$parent.selectedStatus"
        class="mb-5"
        clear-icon="mdi-close-circle"
        item-value="id"
        item-text="name"
        chips
        filled
        outlined
        multiple
        clearable
        hide-details
        return-object
        deletable-chips
        small-chips
        :items="status"
        :disabled="isLoading"
        :label="$t('dashboard.tabFilter.label.status')"
      />

      <v-autocomplete
        v-model="$parent.$parent.$parent.$parent.selectedCitys"
        prepend-inner-icon="mdi-magnify"
        key="id"
        class="mb-5"
        clear-icon="mdi-close-circle"
        item-value="id"
        item-text="name"
        chips
        filled
        outlined
        multiple
        clearable
        hide-details
        return-object
        deletable-chips
        small-chips
        :items="citiesItems"
        :loading="searchCitiesIsLoading"
        :disabled="isLoading"
        :label="$t('dashboard.tabFilter.label.cities')"
        :no-data-text="$t('dashboard.useTheSearch')"
        :search-input.sync="searchCities"
      >
        <template v-slot:selection="data">
          <v-chip small>
            {{ data.item.name }} / {{ data.item.state.acronym }}
          </v-chip>
        </template>
        <template v-slot:item="data">
          {{ data.item.name }} / {{ data.item.state.acronym }}
        </template>
      </v-autocomplete>

      <v-autocomplete
        v-model="$parent.$parent.$parent.$parent.selectedClients"
        prepend-inner-icon="mdi-magnify"
        class="mb-5"
        clear-icon="mdi-close-circle"
        item-value="id"
        item-text="name"
        chips
        filled
        outlined
        multiple
        clearable
        hide-details
        return-object
        deletable-chips
        small-chips
        :items="customers"
        :loading="searchCustomersIsLoading"
        :disabled="isLoading"
        :label="$t('dashboard.tabFilter.label.clients')"
        :no-data-text="$t('dashboard.useTheSearch')"
        :search-input.sync="searchCustomers"
      />

      <v-autocomplete
        v-model="$parent.$parent.$parent.$parent.selectedHosts"
        prepend-inner-icon="mdi-magnify"
        class="mb-5"
        clear-icon="mdi-close-circle"
        item-value="id"
        item-text="name"
        chips
        filled
        outlined
        multiple
        clearable
        hide-details
        return-object
        deletable-chips
        small-chips
        :items="hosts"
        :loading="searchHostsIsLoading"
        :disabled="isLoading"
        :label="$t('dashboard.tabFilter.label.hosts')"
        :no-data-text="$t('dashboard.useTheSearch')"
        :search-input.sync="searchHosts"
      />

      <v-autocomplete
        v-model="$parent.$parent.$parent.$parent.selectedCountry"
        class="mb-5"
        clear-icon="mdi-close-circle"
        item-value="value"
        item-text="name"
        chips
        filled
        outlined
        clearable
        hide-details
        deletable-chips
        small-chips
        :items="countries"
        :disabled="isLoading"
        :label="$t('dashboard.tabFilter.label.country')"
      />

      <v-switch
        v-model="$parent.$parent.$parent.$parent.isFindHostMode"
        :label="$t('dashboard.tabFilter.button.hostRoutes')"
        color="primary"
        class="mt-5"
        text
      />

      <v-row>
        <v-col cols="12" sm="6" class="pb-0">
          <v-btn block @click="clear" elevation="0">
            {{ $t('dashboard.tabFilter.button.clear') }}
          </v-btn>
        </v-col>
        <v-col cols="12" sm="6" class="pb-0">
          <v-btn
            block
            color="primary"
            elevation="0"
            :loading="isLoading"
            :disabled="isLoading"
            @click="$parent.$parent.$parent.$parent.updateFilter(selectedPeriod)"
          >
            {{ $t('dashboard.tabFilter.button.applyFilters') }}
          </v-btn>
        </v-col>
      </v-row>
    </v-card>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

export default {
  name: 'Filters',

  props: {
    isLoading: {
      type: Boolean,
    },
    isFindHostMode: {
      type: Boolean,
    },
    filterOptions: {
      type: Object,
    },
    selectedPeriod: {
      type: [String, Number],
    },
    selectedStatus: {
      type: Array,
    },
    selectedCitys: {
      type: Array,
    },
    selectedTypes: {
      type: Array,
    },
    selectedClients: {
      type: Array,
    },
    selectedHosts: {
      type: Array,
    },
    selectedCountry: {
      type: String,
    },
    clear: {
      type: Function,
    },
  },

  watch: {
    searchCities(term) {
      if (!term) return
      if (term?.length < 3) return

      clearTimeout(this._timer)
      this.searchCitiesIsLoading = true

      this._timer = setTimeout(() => {
        this.searchCity(term).finally(() => {
          this.searchCitiesIsLoading = false
          this.setCitiesItems()
        })
      }, 1000)
    },

    searchCustomers(term) {
      if (!term) return
      if (term?.length < 3) return

      clearTimeout(this._timer)
      this.searchCustomersIsLoading = true

      this._timer = setTimeout(() => {
        this.fetchCustomers(term).finally(() => this.searchCustomersIsLoading = false)
      }, 1000)
    },

    searchHosts(term) {
      if (!term) return
      if (term?.length < 3) return

      clearTimeout(this._timer)
      this.searchHostsIsLoading = true

      this._timer = setTimeout(() => {
        this.fetchHosts(term).finally(() => this.searchHostsIsLoading = false)
      }, 1000)
    }
  },

  methods: {
    ...mapActions(['fetchServiceTypes', 'searchCity', 'fetchCustomers', 'fetchHosts']),

    setCitiesItems() {
      const mappedCities = this.cities
      const selectedCities = this.$parent.$parent.$parent.$parent.selectedCitys
      this.citiesItems = [...mappedCities, ...selectedCities]
    }
  },

  computed: {
    ...mapGetters(['serviceTypes', 'cities', 'customers', 'hosts']),
  },

  data() {
    return {
      _timer: null,

      searchCities: null,
      searchCitiesIsLoading: false,
      citiesItems: [],

      searchCustomers: null,
      searchCustomersIsLoading: false,

      searchHosts: null,
      searchHostsIsLoading: false,

      status: [
        { value: 'OPEN', name: this.$t('appointment.status.OPEN') },
        { value: 'SCHEDULED', name: this.$t('appointment.status.SCHEDULED') },
        { value: 'DONE', name: this.$t('appointment.status.DONE') },
        { value: 'CANCELED', name: this.$t('appointment.status.CANCELED') },
      ],
      countries: [
        { value: 'BR', name: this.$t('language.brazil') },
        { value: 'AU', name: this.$t('language.australia') },
      ]
    }
  },

  mounted() {
    this.fetchServiceTypes()
  }
}
</script>
